import Autocomplete, { type AutocompleteProps } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

export type IconSelectOption<T extends string | number = string> = {
  value: T;
  label: string;
  icon: string;
  i18nKey?: string;
};

export function withIcon<T extends string | number>(
  options: Array<{ value: T; label: string }>,
  icon: string | ((option: { value: T; label: string }) => string),
): IconSelectOption<T>[] {
  return options.map((option) => ({
    ...option,
    icon: typeof icon === "function" ? icon(option) : icon,
  }));
}

export function stringOptionsWithIcon(values: readonly string[], icon: string): IconSelectOption<string>[] {
  return values.map((value) => ({ value, label: value, icon }));
}

type IconAutocompleteProps<T extends string | number> = {
  options: IconSelectOption<T>[];
  value: T | null | undefined;
  onChange: (value: T | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  helperText?: string;
  noOptionsText?: string;
  loading?: boolean;
  filterOptions?: AutocompleteProps<IconSelectOption<T>, false, boolean, false>["filterOptions"];
  sx?: SxProps<Theme>;
};

export default function IconAutocomplete<T extends string | number>({
  options,
  value,
  onChange,
  label,
  placeholder,
  required = false,
  disabled = false,
  disableClearable = false,
  fullWidth = true,
  size = "small",
  helperText,
  noOptionsText,
  loading = false,
  filterOptions,
  sx,
}: IconAutocompleteProps<T>) {
  const { t } = useTranslation();
  const selected =
    options.find((option) => option.value === value) ??
    (disableClearable ? options[0] ?? null : null);
  const optionText = (option: IconSelectOption<T>) =>
    option.i18nKey ? t(option.i18nKey, { defaultValue: option.label }) : option.label;

  return (
    <Autocomplete
      options={options}
      value={selected}
      disabled={disabled}
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      getOptionLabel={optionText}
      isOptionEqualToValue={(option, next) => option.value === next.value}
      onChange={(_event, next) => onChange(next?.value ?? null)}
      noOptionsText={noOptionsText ?? t("common.noOptions")}
      loading={loading}
      filterOptions={filterOptions}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key?: string };
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Icon fontSize="small" color="action">
              {option.icon}
            </Icon>
            <Typography variant="body2">{optionText(option)}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder || t("common.select")}
          required={required}
          helperText={helperText}
          variant="outlined"
          size={size}
        />
      )}
    />
  );
}
